/**
 * Microsoft (MSAL) authentication provider.
 *
 * Wraps `@azure/msal-react` and exposes the `IAuthContext` interface.
 * This is an extraction of the original `AuthContext.tsx` logic with no
 * behavioural changes.
 */

import { BrowserAuthError, InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getApiRequest, getLoginRequest } from "@/config/msal";
import { setTokenGetter } from "@/lib/api";
import type { IAuthContext } from "./types";
import { useProfileLoader } from "./useProfileLoader";

export function useMsalAuth(): IAuthContext {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const { user, profile, platformBlocked, fetchAndSetProfile, clearProfile, updatePreferences } = useProfileLoader();

  // Mutex: shared promise for interactive token acquisition.
  const interactivePromiseRef = useRef<Promise<string> | null>(null);

  // Ref to avoid stale-closure issues with accounts.
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  const getAccessToken = useCallback(async (): Promise<string> => {
    const currentAccounts = accountsRef.current;
    if (currentAccounts.length === 0) {
      throw new Error("No authenticated account");
    }

    const apiRequest = getApiRequest();
    if (apiRequest.scopes.length === 0) {
      return "";
    }

    // Silent first
    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: currentAccounts[0],
      });
      return response.accessToken;
    } catch (silentError) {
      if (!(silentError instanceof InteractionRequiredAuthError) && !(silentError instanceof BrowserAuthError)) {
        throw silentError;
      }
    }

    // Interactive fallback (shared promise)
    if (interactivePromiseRef.current) {
      return interactivePromiseRef.current;
    }

    interactivePromiseRef.current = (async () => {
      try {
        const response = await instance.acquireTokenPopup(apiRequest);
        return response.accessToken;
      } catch {
        await instance.acquireTokenRedirect(apiRequest);
        return "";
      }
    })().finally(() => {
      interactivePromiseRef.current = null;
    });

    return interactivePromiseRef.current;
  }, [instance]);

  // Register token getter for the API client.
  useEffect(() => {
    setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // Load profile when authenticated.
  useEffect(() => {
    async function loadProfile() {
      if (!isAuthenticated || inProgress !== InteractionStatus.None) return;
      try {
        setIsLoading(true);
        setError(null);
        await fetchAndSetProfile();
      } catch (err) {
        console.error("Failed to load user profile:", err);
        setError(err instanceof Error ? err : new Error("Failed to load profile"));
      } finally {
        setIsLoading(false);
      }
    }
    loadProfile();
  }, [isAuthenticated, inProgress, fetchAndSetProfile]);

  // Resolve loading state when MSAL is idle and user is not authenticated.
  useEffect(() => {
    if (inProgress === InteractionStatus.None && !isAuthenticated) {
      setIsLoading(false);
    }
  }, [inProgress, isAuthenticated]);

  const login = useCallback(async () => {
    try {
      await instance.loginPopup(getLoginRequest());
    } catch (err) {
      console.warn("Popup login failed, falling back to redirect:", err);
      try {
        await instance.loginRedirect(getLoginRequest());
      } catch (redirectErr) {
        console.error("Login failed:", redirectErr);
        setError(redirectErr instanceof Error ? redirectErr : new Error("Login failed"));
      }
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutPopup();
      clearProfile();
    } catch {
      try {
        await instance.logoutRedirect();
      } catch (redirectErr) {
        console.error("Logout failed:", redirectErr);
      }
    }
  }, [instance, clearProfile]);

  const refreshProfile = useCallback(async () => {
    try {
      await fetchAndSetProfile();
    } catch (err) {
      console.error("Failed to refresh profile:", err);
    }
  }, [fetchAndSetProfile]);

  const isLoadingResolved = isLoading || inProgress !== InteractionStatus.None;

  return useMemo<IAuthContext>(
    () => ({
      user,
      profile,
      platformBlocked,
      isAuthenticated,
      isLoading: isLoadingResolved,
      error,
      login,
      logout,
      getAccessToken,
      refreshProfile,
      updatePreferences,
      providerName: "Microsoft",
    }),
    [
      user,
      profile,
      platformBlocked,
      isAuthenticated,
      isLoadingResolved,
      error,
      login,
      logout,
      getAccessToken,
      refreshProfile,
      updatePreferences,
    ],
  );
}
